import React, {
    ChangeEventHandler,
    ChangeEvent,
    MouseEventHandler,
    useMemo,
} from "react";
import { Moment } from "moment";
import { SelectProps, SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { IAlarmState } from "@core/interfaces/ISettings";
import { alarmAction } from "@core/interfaces/alarm-action";
import { BindedHandler } from "@core/interfaces/binded-handler";
import { mode } from "@core/interfaces/mode";
import { lang } from "@lang/interfaces/lang";
import TrackerSettingsComponent from "./Tracker-settings.component";
import PeriodicSettingsComponent from "./Periodic-settings.component";
import AlarmSettingsComponent from "./Alarm-settings.component";
import { IDateInput, INumberInput } from "./useDeviceSettings";
import useDictionary from "@ui/hooks/useDictionary";
import SelectComponent from "@ui/components/Select/Select.component";
import { Alert } from "@mui/material";

export interface IProps {
    alarmAction: IAlarmState<number>;
    alarmActions: alarmAction[];
    alarmState: IAlarmState<boolean>;
    wakeUpTime: IAlarmState<IDateInput>;
    wakeUpActiveTime: IAlarmState<INumberInput>;
    coordTxPeriod: number | null;
    coordTxPeriodError: boolean;
    periodicActiveTime: number | null;
    periodicActiveTimeError: boolean;
    periodicSleepDays: number | null;
    periodicSleepDaysError: boolean;
    periodicSleepHours: number | null;
    periodicSleepHoursError: boolean;
    periodicSleepMinutes: number | null;
    periodicSleepMinutesError: boolean;
    periodicLocationPeriod: number | null;
    periodicLocationPeriodError: boolean;
    mode: mode;
    modes: mode[];
    lang: lang;
    timezone?: number;
    onInputClick: () => void;
    onModeChange: SelectProps<mode>["onChange"];
    onAlarmStateChange: MouseEventHandler<HTMLButtonElement>;
    onWakeUpChange: BindedHandler<Moment>;
    onWakeUpError: BindedHandler<unknown>;
    onWakeUpActiveTimeChange: BindedHandler<ChangeEvent<HTMLInputElement>>;
    onCoordTxPeriodChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicActiveTimeChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepDaysChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepHoursChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepMinutesChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicLocationPeriodChange: ChangeEventHandler<HTMLInputElement>;
    onAlarmActionChange: BindedHandler<SelectChangeEvent<number>>;
}

const Component: React.FC<IProps> = ({
    alarmAction,
    alarmActions,
    alarmState,
    wakeUpTime,
    wakeUpActiveTime,
    coordTxPeriod,
    coordTxPeriodError,
    periodicActiveTime,
    periodicActiveTimeError,
    periodicSleepDays,
    periodicSleepDaysError,
    periodicSleepHours,
    periodicSleepHoursError,
    periodicSleepMinutes,
    periodicSleepMinutesError,
    periodicLocationPeriod,
    periodicLocationPeriodError,
    mode,
    modes,
    lang,
    timezone,
    onInputClick,
    onModeChange,
    onAlarmStateChange,
    onWakeUpChange,
    onWakeUpError,
    onWakeUpActiveTimeChange,
    onCoordTxPeriodChange,
    onPeriodicActiveTimeChange,
    onPeriodicSleepDaysChange,
    onPeriodicSleepHoursChange,
    onPeriodicSleepMinutesChange,
    onPeriodicLocationPeriodChange,
    onAlarmActionChange,
}: IProps) => {
    const getWord = useDictionary();
    const getSettings = () => {
        switch (mode) {
            case "tracker":
                return (
                    <>
                        <TrackerSettingsComponent
                            lang={lang}
                            coordTxPeriod={coordTxPeriod}
                            coordTxPeriodError={coordTxPeriodError}
                            onInputClick={onInputClick}
                            onCoordTxPeriodChange={onCoordTxPeriodChange}
                        />
                    </>
                );
            case "periodic":
                return (
                    <>
                        <PeriodicSettingsComponent
                            lang={lang}
                            periodicActiveTime={periodicActiveTime}
                            periodicActiveTimeError={periodicActiveTimeError}
                            periodicSleepDays={periodicSleepDays}
                            periodicSleepDaysError={periodicSleepDaysError}
                            periodicSleepHours={periodicSleepHours}
                            periodicSleepHoursError={periodicSleepHoursError}
                            periodicSleepMinutes={periodicSleepMinutes}
                            periodicSleepMinutesError={
                                periodicSleepMinutesError
                            }
                            periodicLocationPeriod={periodicLocationPeriod}
                            periodicLocationPeriodError={
                                periodicLocationPeriodError
                            }
                            onPeriodicActiveTimeChange={
                                onPeriodicActiveTimeChange
                            }
                            onPeriodicSleepDaysChange={
                                onPeriodicSleepDaysChange
                            }
                            onPeriodicSleepHoursChange={
                                onPeriodicSleepHoursChange
                            }
                            onPeriodicSleepMinutesChange={
                                onPeriodicSleepMinutesChange
                            }
                            onPeriodicLocationPeriodChange={
                                onPeriodicLocationPeriodChange
                            }
                            onInputClick={onInputClick}
                        />
                    </>
                );
            case "alarm":
                return (
                    <>
                        <AlarmSettingsComponent
                            timezone={timezone}
                            lang={lang}
                            alarmState={alarmState}
                            alarmAction={alarmAction}
                            alarmActions={alarmActions}
                            wakeUpTime={wakeUpTime}
                            wakeUpActiveTime={wakeUpActiveTime}
                            onAlarmStateChange={onAlarmStateChange}
                            onWakeUpChange={onWakeUpChange}
                            onWakeUpError={onWakeUpError}
                            onWakeUpActiveTimeChange={onWakeUpActiveTimeChange}
                            onAlarmActionChange={onAlarmActionChange}
                            onInputClick={onInputClick}
                        />
                    </>
                );
            default:
                return <></>;
        }
    };
    const settings = getSettings();

    const tooltipText = useMemo(
        () =>
            new Array(3)
                .fill(0)
                .map((v, i) => `${i + 1}. ${getWord(`modeTooltip_${i}`)}.`),
        []
    );

    const [open, setOpen] = React.useState(false);
    const handleClose = () => setOpen(false);

    React.useEffect(() => {
        if (mode === 'tracker') {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [mode]);
   

    return (
        <FormControl className="device-settings__form" component="form">
            <SelectComponent
                label="operationMode"
                name="operation-mode"
                lang={lang}
                description={tooltipText}
                value={mode}
                values={modes}
                onChange={onModeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            {mode === "tracker" && (
                <Alert
                    className="device-settings__tracker-alert "
                    severity="warning"
                >
                    {getWord('trackerModeAttention')}
                </Alert>
            )}
            {settings}
        </FormControl>
    );
};

const ModeSettingsComponent = React.memo(Component);

export default ModeSettingsComponent;
